import React, { useState, useEffect } from 'react';
import {
  RefreshCcw, Eye, AlertCircle, TrendingUp, Calendar, Briefcase, Clock,
  Star, Building2, Users, BarChart3, Award, ChevronRight, X
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { useAuth } from '../Context/AuthContext';
import { getApiUrl } from '../utils/api';

import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/Components/ui/card';
import { Badge } from '@/Components/ui/badge';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Skeleton } from '@/Components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/Components/ui/avatar';
import { Separator } from '@/Components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/Components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/Components/ui/table';

export default function Dashboard() {
  const { isSuperAdmin } = useAuth();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nameQuery, setNameQuery] = useState('');
  const [roleQuery, setRoleQuery] = useState('');
  const [experienceQuery, setExperienceQuery] = useState('');

  // Ratings & feedback state
  const [ratingsSummary, setRatingsSummary] = useState({ avg_rating: null, total_count: 0, grouped: [] });
  const [feedbacks, setFeedbacks] = useState([]);       // preview (first N)
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  const [allFeedbacks, setAllFeedbacks] = useState([]);
  const [showAllFeedbackModal, setShowAllFeedbackModal] = useState(false);

  useEffect(() => {
    fetchDashboardStats();
    fetchRatingsAndFeedback(5); // fetch first 5 on mount
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const adminEmail = sessionStorage.getItem('admin_email') || localStorage.getItem('admin_email');

      const response = await fetch(getApiUrl('/api/admin/dashboard-stats'), {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Email': adminEmail || ''
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 401) {
          throw new Error('Not authenticated. Please login as admin first.');
        }
        throw new Error(errorData.error || 'Failed to fetch dashboard statistics');
      }

      const data = await response.json();
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch ratings and feedback.
   * @param {number} limit number of feedback rows to fetch (use 0 or omit to fetch all)
   */
  const fetchRatingsAndFeedback = async (limit = 5, offset = 0) => {
    setFeedbackLoading(true);
    setFeedbackError(null);

    try {
      const adminEmail = sessionStorage.getItem('admin_email') || localStorage.getItem('admin_email') || '';
      // limit=0 returns none (we treat >0)
      const q = (typeof limit === 'number' && limit > 0) ? `?limit=${limit}&offset=${offset}` : `?limit=0&offset=${offset}`;
      const res = await fetch(getApiUrl(`/api/admin/feedbacks${q}`), {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Email': adminEmail
        }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to load feedbacks');
      }
      const payload = await res.json();
      // payload: { avg_rating, total_count, grouped: [...], feedbacks: [...] }
      setRatingsSummary({
        avg_rating: payload.avg_rating ?? null,
        total_count: payload.total_count ?? 0,
        grouped: payload.grouped ?? []
      });
      setFeedbacks(Array.isArray(payload.feedbacks) ? payload.feedbacks : []);
    } catch (err) {
      console.error('fetchRatingsAndFeedback error', err);
      setFeedbackError(err.message || 'Error loading feedbacks');
      setRatingsSummary({ avg_rating: null, total_count: 0, grouped: [] });
      setFeedbacks([]);
    } finally {
      setFeedbackLoading(false);
    }
  };

  // Fetch all feedbacks and open modal
  const handleViewAllFeedback = async () => {
    setFeedbackLoading(true);
    setFeedbackError(null);
    try {
      const adminEmail = sessionStorage.getItem('admin_email') || localStorage.getItem('admin_email') || '';
      // use all=1 param to request entire set
      const res = await fetch(getApiUrl(`/api/admin/feedbacks?all=1`), {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Email': adminEmail
        }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to load all feedbacks');
      }
      const payload = await res.json();
      setAllFeedbacks(Array.isArray(payload.feedbacks) ? payload.feedbacks : []);
      setShowAllFeedbackModal(true);
    } catch (err) {
      console.error('handleViewAllFeedback error', err);
      setFeedbackError(err.message || 'Failed to fetch all feedbacks');
    } finally {
      setFeedbackLoading(false);
    }
  };

  // Helper to render rating number nicely
  const renderAvgRating = () => {
    if (!ratingsSummary) return '—';
    const avg = ratingsSummary.avg_rating;
    return avg === null || avg === undefined ? '—' : Number(avg).toFixed(1);
  };

  const pieData = [
    { name: 'Shortlisted', value: stats.shortlistedCandidates, color: '#16a34a' },
    { name: 'Rejected', value: stats.rejectedCandidates, color: '#dc2626' },
    { name: 'Inactive', value: stats.inactiveCandidates, color: '#6b7280' },
  ].filter(item => item.value > 0);

  const lineChartData = stats.lineChartData?.length > 0 ? stats.lineChartData : [{ date: 'No Data', applications: 0 }];

  const normalizeFilterValue = (value) => String(value || '').trim().toLowerCase();

  const getCandidateExperienceText = (candidate) => (
    [
      candidate?.experience,
      candidate?.experienceText,
      candidate?.exp_yrs,
      candidate?.relevant_exp_yrs,
      candidate?.expYears,
      candidate?.relevantExpYears,
    ]
      .filter(value => value !== undefined && value !== null && String(value).trim() !== '')
      .join(' ')
      .toLowerCase()
  );

  const filterDashboardCandidates = (items = []) => {
    const normalizedNameQuery = normalizeFilterValue(nameQuery);
    const normalizedRoleQuery = normalizeFilterValue(roleQuery);
    const normalizedExperienceQuery = normalizeFilterValue(experienceQuery);

    return (items || []).filter((candidate) => {
      const candidateName = normalizeFilterValue(candidate?.candidateName || candidate?.name);
      const candidateRole = normalizeFilterValue(candidate?.jobTitle || candidate?.role || candidate?.designation);
      const candidateExperience = getCandidateExperienceText(candidate);

      const matchesName = !normalizedNameQuery || candidateName.includes(normalizedNameQuery);
      const matchesRole = !normalizedRoleQuery || candidateRole.includes(normalizedRoleQuery);
      const matchesExperience =
        !normalizedExperienceQuery || candidateExperience.includes(normalizedExperienceQuery);

      return matchesName && matchesRole && matchesExperience;
    });
  };

  const filteredAwaitingShortlistDecision = filterDashboardCandidates(stats.awaitingShortlistDecision);
  const filteredReadyToSendToClient = filterDashboardCandidates(stats.readyToSendToClient);
  const filteredInterviewScheduled = filterDashboardCandidates(stats.interviewScheduled);
  const filteredNeedsInterviewScheduling = filterDashboardCandidates(stats.needsInterviewScheduling);
  const filteredRecentTestResults = filterDashboardCandidates(stats.recentTestResults);
  const filteredSearchCandidates = filterDashboardCandidates(stats.searchCandidates);

  const hasActiveCandidateFilters = Boolean(
    nameQuery.trim() || roleQuery.trim() || experienceQuery.trim()
  );

  const hasAnyFilteredDashboardCandidates =
    filteredSearchCandidates.length > 0 ||
    filteredAwaitingShortlistDecision.length > 0 ||
    filteredReadyToSendToClient.length > 0 ||
    filteredInterviewScheduled.length > 0 ||
    filteredNeedsInterviewScheduling.length > 0 ||
    filteredRecentTestResults.length > 0;

  const resetCandidateFilters = () => {
    setNameQuery('');
    setRoleQuery('');
    setExperienceQuery('');
  };

  // Score badge helper
  const scoreBadgeClass = (score) => {
    if (score >= 70) return 'bg-green-100 text-green-700';
    if (score >= 50) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  // Status badge variant helper
  const statusBadgeVariant = (status) => {
    if (status === 'Shortlisted') return 'bg-green-100 text-green-700';
    if (status === 'Rejected') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-600';
  };

  const candidateNameButtonClass =
    'font-semibold text-sm text-slate-900 mb-0.5 p-0 bg-transparent border-none cursor-pointer hover:text-indigo-600 hover:underline text-left';

  const openCandidateSource = (jobId) => {
    if (!jobId) return;
    window.location.href = `/all-candidates?jobID=${encodeURIComponent(jobId)}`;
  };

  if (loading) {
    return (
      <div className="p-6 flex flex-col gap-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-3 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 flex flex-col gap-6 max-w-7xl mx-auto">
        <Card className="border-red-200">
          <CardContent className="flex items-center justify-center gap-4 py-16">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-500">{error}</span>
            <Button variant="default" size="sm" onClick={fetchDashboardStats}>
              <RefreshCcw className="w-4 h-4 mr-1" /> Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Super Admin Dashboard ───────────────────────────────────────────────────
  if (isSuperAdmin) {
    return (
      <div className="p-6 flex flex-col gap-6 max-w-7xl mx-auto">

        {/* HEADER METRICS */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">

              {/* Total Jobs */}
              <Card className="relative overflow-hidden">
                <CardHeader className="pb-2">
                  <CardDescription className="text-sm font-medium text-slate-500">Total Jobs</CardDescription>
                  <CardTitle className="text-3xl font-bold text-slate-800">{stats.totalJobs || 0}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-slate-400">All job postings</p>
                </CardContent>
                <div className="absolute right-4 top-4 w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-indigo-600" />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-16 w-7 h-7 text-slate-400 hover:text-blue-500"
                  onClick={() => { fetchDashboardStats(); fetchRatingsAndFeedback(5); }}
                >
                  <RefreshCcw className="w-4 h-4" />
                </Button>
              </Card>

              {/* Total Companies */}
              <Card className="relative overflow-hidden">
                <CardHeader className="pb-2">
                  <CardDescription className="text-sm font-medium text-slate-500">Total Companies</CardDescription>
                  <CardTitle className="text-3xl font-bold text-slate-800">{stats.totalCompanies || 0}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-slate-400">Registered companies</p>
                </CardContent>
                <div className="absolute right-4 top-4 w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-indigo-600" />
                </div>
              </Card>

              {/* Interviews Completed */}
              <Card className="relative overflow-hidden">
                <CardHeader className="pb-2">
                  <CardDescription className="text-sm font-medium text-slate-500">Interviews Completed</CardDescription>
                  <CardTitle className="text-3xl font-bold text-slate-800">{stats.interviewsCompleted || 0}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-slate-400">Completed interviews</p>
                </CardContent>
                <div className="absolute right-4 top-4 w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                </div>
              </Card>

              {/* Total Candidates */}
              <Card className="relative overflow-hidden">
                <CardHeader className="pb-2">
                  <CardDescription className="text-sm font-medium text-slate-500">Total Candidates</CardDescription>
                  <CardTitle className="text-3xl font-bold text-slate-800">{stats.totalCandidates || 0}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-slate-400">All applicants</p>
                </CardContent>
                <div className="absolute right-4 top-4 w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
              </Card>
            </div>

            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={lineChartData}>
                <XAxis dataKey="date" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Line type="monotone" dataKey="applications" stroke="#2563eb" strokeWidth={3} dot={{ fill: '#2563eb', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* CANDIDATE DISTRIBUTION */}
        <Card>
          <CardHeader className="pb-3 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              Candidate Distribution ({stats.totalCandidates || 0})
            </h2>
          </CardHeader>
          <CardContent className="pt-4">
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2}>
                    {pieData.map((e, i) => (<Cell key={i} fill={e.color} />))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-slate-400 py-6 text-sm italic">No data available</div>
            )}
          </CardContent>
        </Card>

        {/* RECENT COMPANIES */}
        <Card>
          <CardHeader className="pb-3 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Recent Companies ({stats.recentCompanies?.length || 0})
            </h2>
          </CardHeader>
          <CardContent className="pt-4">
            {stats.recentCompanies?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Created On</TableHead>
                    <TableHead>Jobs</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.recentCompanies.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{row.company}</TableCell>
                      <TableCell className="text-slate-500">{row.createdOn}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{row.jobsPosted}</Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-8 h-8 text-blue-500 hover:text-blue-700"
                          onClick={() => window.location.href = `/company-admin-home?companyId=${row.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center text-slate-400 py-6 text-sm italic">No companies</div>
            )}
          </CardContent>
        </Card>

        {/* TOP TEST RESULTS */}
        {stats.recentTestResults?.length > 0 && (
          <Card>
            <CardHeader className="pb-3 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Award className="w-5 h-5 text-indigo-600" />
                Top Test Results ({stats.recentTestResults.length})
              </h2>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="max-h-80 overflow-y-auto flex flex-col gap-3 pr-1">
                {stats.recentTestResults.map((test, i) => (
                  <div key={i} className="flex items-start gap-4 bg-slate-50 rounded-lg p-4 border border-slate-100">
                    <Avatar className="w-9 h-9 shrink-0">
                      <AvatarFallback className="bg-indigo-100 text-indigo-700 text-sm font-semibold">
                        {test.candidateName?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <button
                        type="button"
                        className={candidateNameButtonClass}
                        onClick={() => openCandidateSource(test.jobId)}
                        title="Open candidate source page"
                      >
                        {test.candidateName}
                      </button>
                      <div className="text-sm text-slate-500">{test.jobTitle} • {test.company}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-400">{test.testDate}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusBadgeVariant(test.status)}`}>
                          {test.status}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-center">
                        <div className="text-xs text-slate-500 font-semibold mb-1">Interview</div>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold ${scoreBadgeClass(test.overallScore)}`}>
                          {test.overallScore}%
                        </span>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-slate-500 font-semibold mb-1">Resume</div>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold ${scoreBadgeClass(test.resumeScore)}`}>
                          {test.resumeScore}%
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-8 h-8 text-blue-500 hover:text-blue-700"
                        onClick={() => openCandidateSource(test.jobId)}
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* RATINGS & FEEDBACK */}
        <Card>
          <CardHeader className="pb-3 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Star className="w-5 h-5 text-indigo-600" />
              Ratings &amp; Candidate Feedback
            </h2>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-extrabold text-slate-800">
                  {ratingsSummary.avg_rating !== null && ratingsSummary.avg_rating !== undefined
                    ? Number(ratingsSummary.avg_rating).toFixed(1)
                    : '—'}
                </span>
                <span className="text-slate-500 font-semibold">/ 5</span>
                <span className="text-slate-400 text-sm ml-2">
                  {ratingsSummary.total_count ? `${ratingsSummary.total_count} reviews` : 'No reviews'}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchRatingsAndFeedback(5)}
                  title="Refresh feedback"
                >
                  <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Refresh
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { fetchDashboardStats(); fetchRatingsAndFeedback(5); }}
                  title="Refresh all"
                >
                  <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Refresh All
                </Button>
              </div>
            </div>

            {/* grouped counts by rating */}
            <div className="flex gap-2 flex-wrap mb-4">
              {Array.isArray(ratingsSummary.grouped) && ratingsSummary.grouped.length > 0 ? (
                ratingsSummary.grouped.map((g) => (
                  <Card key={g.rating_value} className="min-w-[110px] bg-slate-50 border border-slate-200">
                    <CardContent className="px-3 py-2">
                      <div className="flex items-center gap-1 text-sm text-slate-500 mb-0.5">
                        <Star className="w-3.5 h-3.5 text-amber-400" />
                        {g.rating_value} star
                      </div>
                      <div className="text-lg font-extrabold text-slate-800">{g.count_users}</div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <p className="text-slate-500 text-sm">No rating breakdown available</p>
              )}
            </div>

            {/* feedback list (preview) */}
            <div className="max-h-[340px] overflow-auto flex flex-col gap-3">
              {feedbackLoading ? (
                <>
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                      <Skeleton className="w-9 h-9 rounded-full shrink-0" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-48" />
                      </div>
                    </div>
                  ))}
                </>
              ) : feedbackError ? (
                <div className="flex items-center gap-2 text-red-500 text-sm py-6 justify-center">
                  <AlertCircle className="w-4 h-4" /> {feedbackError}
                </div>
              ) : feedbacks.length > 0 ? (
                feedbacks.map((fb) => (
                  <div key={fb.id} className="flex items-start gap-3 bg-slate-50 rounded-lg p-3 border border-slate-100">
                    <Avatar className="w-9 h-9 shrink-0">
                      <AvatarFallback className="bg-amber-100 text-amber-700 text-sm font-semibold">
                        {fb.username?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span className="font-semibold text-slate-900 text-sm">{fb.username}</span>
                        <Badge variant="secondary" className="shrink-0">
                          {fb.rating !== null && fb.rating !== undefined ? fb.rating : '—'} / 5
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-400 mb-1">{fb.user_email}</div>
                      <p className={`text-sm ${fb.feedback_text ? 'text-slate-700' : 'text-slate-400 italic'}`}>
                        {fb.feedback_text || '(no text feedback)'}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-slate-400 py-6 text-sm italic">No feedback available</div>
              )}
            </div>

            {ratingsSummary.total_count > 5 && (
              <div className="flex justify-center mt-4">
                <Button
                  variant="outline"
                  className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                  onClick={handleViewAllFeedback}
                  disabled={feedbackLoading}
                >
                  {feedbackLoading ? 'Loading...' : (
                    <>View all feedback <ChevronRight className="w-4 h-4 ml-1" /></>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* All feedback modal */}
        <Dialog open={showAllFeedbackModal} onOpenChange={setShowAllFeedbackModal}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-indigo-600" />
                All Feedback ({allFeedbacks.length})
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-3 mt-2">
              {allFeedbacks.length === 0 ? (
                <div className="text-center text-slate-400 py-6 text-sm italic">No feedback available</div>
              ) : (
                allFeedbacks.map((fb) => (
                  <div key={fb.id} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
                    <Avatar className="w-9 h-9 shrink-0">
                      <AvatarFallback className="bg-amber-100 text-amber-700 text-sm font-semibold">
                        {fb.username?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span className="font-semibold text-slate-900 text-sm">{fb.username}</span>
                        <Badge variant="secondary" className="shrink-0">
                          {fb.rating !== null && fb.rating !== undefined ? fb.rating : '—'} / 5
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-400 mb-1">{fb.user_email}</div>
                      <p className={`text-sm ${fb.feedback_text ? 'text-slate-700' : 'text-slate-400 italic'}`}>
                        {fb.feedback_text || '(no text feedback)'}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

      </div>
    );
  }

  // ─── Company Admin Dashboard ─────────────────────────────────────────────────
  return (
    <div className="p-6 flex flex-col gap-6 max-w-7xl mx-auto">

      {/* MY ASSIGNMENTS */}
      <Card>
        <CardHeader className="pb-3 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-indigo-600" />
            My Assignments
          </h2>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            {/* Assigned Companies */}
            <Card className="relative overflow-hidden border-0 shadow-none" style={{ background: 'linear-gradient(135deg, #2563eb, #1e40af)' }}>
              <CardHeader className="pb-2">
                <CardDescription className="text-sm font-medium text-blue-100">Assigned Companies</CardDescription>
                <CardTitle className="text-3xl font-bold text-white">{stats.assignedCompanies || 0}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-blue-200">Companies managed</p>
              </CardContent>
              <div className="absolute right-4 top-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
            </Card>

            {/* Individual JDs */}
            <Card className="relative overflow-hidden border-0 shadow-none" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
              <CardHeader className="pb-2">
                <CardDescription className="text-sm font-medium text-emerald-100">Individual JDs</CardDescription>
                <CardTitle className="text-3xl font-bold text-white">{stats.assignedJobs || 0}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-emerald-200">Directly assigned</p>
              </CardContent>
              <div className="absolute right-4 top-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
            </Card>

            {/* Total Managing */}
            <Card className="relative overflow-hidden border-0 shadow-none" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
              <CardHeader className="pb-2">
                <CardDescription className="text-sm font-medium text-amber-100">Total Managing</CardDescription>
                <CardTitle className="text-3xl font-bold text-white">{stats.totalJobsManaging || 0}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-amber-200">Active positions</p>
              </CardContent>
              <div className="absolute right-4 top-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
            </Card>
          </div>

          {stats.companyBreakdown?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
              {stats.companyBreakdown.map((c, i) => (
                <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                  <div className="font-semibold text-sm text-slate-900 mb-2">{c.companyName}</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{c.totalJobs} jobs</Badge>
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">{c.totalApplications} apps</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3 border-b border-slate-100">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              Candidate Filters
            </h2>
            {hasActiveCandidateFilters && (
              <Button variant="outline" size="sm" onClick={resetCandidateFilters}>
                Clear Filters
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-2">
              <div className="text-sm font-medium text-slate-700">Search by Name</div>
              <Input
                value={nameQuery}
                onChange={(e) => setNameQuery(e.target.value)}
                placeholder="Candidate name"
              />
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-slate-700">Search by Role</div>
              <Input
                value={roleQuery}
                onChange={(e) => setRoleQuery(e.target.value)}
                placeholder="Role or job title"
              />
            </div>
            <div className="space-y-2">
              <div className="text-sm font-medium text-slate-700">Search by Experience</div>
              <Input
                value={experienceQuery}
                onChange={(e) => setExperienceQuery(e.target.value)}
                placeholder="e.g. 3, 5, 7 years"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {hasActiveCandidateFilters && filteredSearchCandidates.length > 0 && (
        <Card className="border-l-4 border-l-indigo-500">
          <CardHeader className="pb-3 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              Candidate Search Results ({filteredSearchCandidates.length})
            </h2>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="max-h-96 overflow-y-auto flex flex-col gap-3 pr-1">
              {filteredSearchCandidates.map((cand, i) => (
                <div key={`${cand.candidateEmail}-${cand.jobId}-${i}`} className="flex items-start gap-4 bg-slate-50 rounded-lg p-4 border border-slate-100">
                  <Avatar className="w-9 h-9 shrink-0">
                    <AvatarFallback className="bg-indigo-100 text-indigo-700 text-sm font-semibold">
                      {cand.candidateName?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <button
                      type="button"
                      className={candidateNameButtonClass}
                      onClick={() => openCandidateSource(cand.jobId)}
                      title="Open candidate source page"
                    >
                      {cand.candidateName}
                    </button>
                    <div className="text-sm text-slate-500">{cand.jobTitle} • {cand.company}</div>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className="text-xs text-slate-400">Applied: {cand.appliedAt}</span>
                      {cand.exp_yrs !== null && cand.exp_yrs !== undefined && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                          {cand.exp_yrs} yrs
                        </span>
                      )}
                      {cand.status && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusBadgeVariant(cand.isRejected ? 'Rejected' : (cand.isShortlisted ? 'Shortlisted' : 'Pending'))}`}>
                          {cand.status}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-center">
                      <div className="text-xs text-slate-500 font-semibold mb-1">Resume</div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold ${scoreBadgeClass(cand.resumeScore)}`}>
                        {cand.resumeScore}%
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-8 h-8 text-blue-500 hover:text-blue-700"
                      onClick={() => openCandidateSource(cand.jobId)}
                      title="View Candidate List"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* THIS WEEK & JOB HEALTH & PERFORMANCE */}
      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
          <h3 className="flex items-center gap-2 text-slate-700 font-semibold text-base mb-4 pb-3 border-b border-slate-100"><TrendingUp size={20} /> This Week</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ background: '#f3f4f6', padding: '12px', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>Applications</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827' }}>{stats.applicationsThisWeek || 0}</div>
            </div>
            <div style={{ background: '#f0fdf4', padding: '12px', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#166534' }}>Shortlisted</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#16a34a' }}>{stats.shortlistedThisWeek || 0}</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
          <h3 className="flex items-center gap-2 text-slate-700 font-semibold text-base mb-4 pb-3 border-b border-slate-100"><AlertCircle size={20} /> Job Health</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ background: '#fef2f2', padding: '12px', borderRadius: '8px', border: '1px solid #fecaca' }}>
              <div style={{ fontSize: '12px', color: '#991b1b' }}>No Applications</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#dc2626' }}>{stats.jobsWithNoApplications || 0}</div>
            </div>
            <div style={{ background: '#f0fdf4', padding: '12px', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#166534' }}>Active (Last 7d)</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#16a34a' }}>{stats.activeJobsLast7Days || 0}</div>
            </div>
            <div style={{ background: '#fef3c7', padding: '12px', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#92400e' }}>Awaiting Interview</div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#d97706' }}>{stats.candidatesAwaitingInterview || 0}</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
          <h3 className="flex items-center gap-2 text-slate-700 font-semibold text-base mb-4 pb-3 border-b border-slate-100"><Clock size={20} /> Performance</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ background: '#eff6ff', padding: '10px', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#1e40af' }}>Shortlist Rate</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2563eb' }}>{stats.shortlistRate || 0}%</div>
            </div>
            <div style={{ background: '#fef2f2', padding: '10px', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#991b1b' }}>Rejection Rate</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626' }}>{stats.rejectionRate || 0}%</div>
            </div>
            <div style={{ background: '#f0fdf4', padding: '10px', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#166534' }}>Interview Conv.</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#16a34a' }}>{stats.interviewConversionRate || 0}%</div>
            </div>
            <div style={{ background: '#fef3c7', padding: '10px', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#92400e' }}>Avg Review</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#d97706' }}>{stats.avgReviewTime || 0}d</div>
            </div>
          </div>
        </div>
      </div> */}

      {/* URGENT - Awaiting Decision */}
      {(filteredAwaitingShortlistDecision.length > 0 || (!hasActiveCandidateFilters && stats.awaitingShortlistDecision?.length > 0)) && (
        <Card className="border-l-4 border-l-red-500">
          <CardHeader className="pb-3 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Awaiting Shortlist Decision ({filteredAwaitingShortlistDecision.length})
              <span className="ml-auto text-xs font-medium text-slate-500">Action required</span>
            </h2>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="max-h-80 overflow-y-auto flex flex-col gap-3 pr-1">
              {filteredAwaitingShortlistDecision.map((cand, i) => (
                <div key={i} className="flex items-start gap-4 bg-slate-50 rounded-lg p-4 border border-slate-100">
                  <Avatar className="w-9 h-9 shrink-0">
                    <AvatarFallback className="bg-red-100 text-red-700 text-sm font-semibold">
                      {cand.candidateName?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <button
                      type="button"
                      className={candidateNameButtonClass}
                      onClick={() => openCandidateSource(cand.jobId)}
                      title="Open candidate source page"
                    >
                      {cand.candidateName}
                    </button>
                    <div className="text-sm text-slate-500">{cand.jobTitle} • {cand.company}</div>
                    <div className="text-xs text-slate-400 mt-1">
                      Interviewed: {cand.interviewDate}{' '}
                      <span className="text-red-500 font-semibold">({cand.daysSinceInterview}d ago)</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-center">
                      <div className="text-xs text-slate-500 font-semibold mb-1">Combined</div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold ${scoreBadgeClass(cand.combinedScore)}`}>
                        {cand.combinedScore}%
                      </span>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-slate-500 font-semibold mb-1">Interview</div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-sm bg-gray-100 text-gray-600">
                        {cand.overallScore}%
                      </span>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-slate-500 font-semibold mb-1">Resume</div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-sm bg-gray-100 text-gray-600">
                        {cand.resumeScore}%
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-8 h-8 text-blue-500 hover:text-blue-700"
                      onClick={() => openCandidateSource(cand.jobId)}
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ready to Send to Client */}
      {(filteredReadyToSendToClient.length > 0 || (!hasActiveCandidateFilters && stats.readyToSendToClient?.length > 0)) && (
        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader className="pb-3 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              Ready to Send to Client ({filteredReadyToSendToClient.length})
              <span className="ml-auto text-xs font-medium text-slate-500">Shortlisted</span>
            </h2>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="max-h-80 overflow-y-auto flex flex-col gap-3 pr-1">
              {filteredReadyToSendToClient.map((cand, i) => (
                <div key={i} className="flex items-start gap-4 bg-slate-50 rounded-lg p-4 border border-slate-100">
                  <Avatar className="w-9 h-9 shrink-0">
                    <AvatarFallback className="bg-emerald-100 text-emerald-700 text-sm font-semibold">
                      {cand.candidateName?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <button
                      type="button"
                      className={candidateNameButtonClass}
                      onClick={() => openCandidateSource(cand.jobId)}
                      title="Open candidate source page"
                    >
                      {cand.candidateName}
                    </button>
                    <div className="text-sm text-slate-500">{cand.jobTitle} • {cand.company}</div>
                    <div className="text-xs text-emerald-600 font-semibold mt-1">Shortlisted {cand.daysSinceShortlist}d ago</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-center">
                      <div className="text-xs text-slate-500 font-semibold mb-1">Interview</div>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold bg-green-100 text-green-700">
                        {cand.overallScore}%
                      </span>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-slate-500 font-semibold mb-1">Resume</div>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold bg-green-100 text-green-700">
                        {cand.resumeScore}%
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-8 h-8 text-blue-500 hover:text-blue-700"
                      onClick={() => openCandidateSource(cand.jobId)}
                      title="View Profile"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Interview Scheduled */}
      {(filteredInterviewScheduled.length > 0 || (!hasActiveCandidateFilters && stats.interviewScheduled?.length > 0)) && (
        <Card className="border-l-4 border-l-amber-400">
          <CardHeader className="pb-3 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Interview Scheduled ({filteredInterviewScheduled.length})
              <span className="ml-auto text-xs font-medium text-slate-500">Awaiting completion</span>
            </h2>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="max-h-80 overflow-y-auto flex flex-col gap-3 pr-1">
              {filteredInterviewScheduled.map((cand, i) => (
                <div key={i} className="flex items-start gap-4 bg-slate-50 rounded-lg p-4 border border-slate-100">
                  <Avatar className="w-9 h-9 shrink-0">
                    <AvatarFallback className="bg-amber-100 text-amber-700 text-sm font-semibold">
                      {cand.candidateName?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <button
                      type="button"
                      className={candidateNameButtonClass}
                      onClick={() => openCandidateSource(cand.jobId)}
                      title="Open candidate source page"
                    >
                      {cand.candidateName}
                    </button>
                    <div className="text-sm text-slate-500">{cand.jobTitle} • {cand.company}</div>
                    <div className="text-xs text-amber-500 font-semibold mt-1">Waiting {cand.daysWaiting}d</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-center">
                      <div className="text-xs text-slate-500 font-semibold mb-1">Resume</div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold ${cand.resumeScore >= 70 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {cand.resumeScore}%
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-8 h-8 text-blue-500 hover:text-blue-700"
                      onClick={() => openCandidateSource(cand.jobId)}
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Needs Interview Scheduling */}
      {(filteredNeedsInterviewScheduling.length > 0 || (!hasActiveCandidateFilters && stats.needsInterviewScheduling?.length > 0)) && (
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-3 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              Needs Interview Scheduling ({filteredNeedsInterviewScheduling.length})
              <span className="ml-auto text-xs font-medium text-slate-500">High scores</span>
            </h2>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="max-h-80 overflow-y-auto flex flex-col gap-3 pr-1">
              {filteredNeedsInterviewScheduling.map((cand, i) => (
                <div key={i} className="flex items-start gap-4 bg-slate-50 rounded-lg p-4 border border-slate-100">
                  <Avatar className="w-9 h-9 shrink-0">
                    <AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-semibold">
                      {cand.candidateName?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <button
                      type="button"
                      className={candidateNameButtonClass}
                      onClick={() => openCandidateSource(cand.jobId)}
                      title="Open candidate source page"
                    >
                      {cand.candidateName}
                    </button>
                    <div className="text-sm text-slate-500">{cand.jobTitle} • {cand.company}</div>
                    <div className="text-xs text-blue-500 font-semibold mt-1">
                      Applied: {cand.appliedAt} ({cand.daysPending}d ago)
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-center">
                      <div className="text-xs text-slate-500 font-semibold mb-1">Resume</div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold ${cand.resumeScore >= 70 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {cand.resumeScore}%
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-8 h-8 text-blue-500 hover:text-blue-700"
                      onClick={() => openCandidateSource(cand.jobId)}
                      title="Schedule Interview"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Test Results */}
      {(filteredRecentTestResults.length > 0 || (!hasActiveCandidateFilters && stats.recentTestResults?.length > 0)) && (
        <Card>
          <CardHeader className="pb-3 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Award className="w-5 h-5 text-indigo-600" />
              Top Test Results ({filteredRecentTestResults.length})
            </h2>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="max-h-80 overflow-y-auto flex flex-col gap-3 pr-1">
              {filteredRecentTestResults.map((test, i) => (
                <div key={i} className="flex items-start gap-4 bg-slate-50 rounded-lg p-4 border border-slate-100">
                  <Avatar className="w-9 h-9 shrink-0">
                    <AvatarFallback className="bg-indigo-100 text-indigo-700 text-sm font-semibold">
                      {test.candidateName?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <button
                      type="button"
                      className={candidateNameButtonClass}
                      onClick={() => openCandidateSource(test.jobId)}
                      title="Open candidate source page"
                    >
                      {test.candidateName}
                    </button>
                    <div className="text-sm text-slate-500">{test.jobTitle} • {test.company}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-slate-400">{test.testDate}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusBadgeVariant(test.status)}`}>
                        {test.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-center">
                      <div className="text-xs text-slate-500 font-semibold mb-1">Interview</div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold ${scoreBadgeClass(test.overallScore)}`}>
                        {test.overallScore}%
                      </span>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-slate-500 font-semibold mb-1">Resume</div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold ${scoreBadgeClass(test.resumeScore)}`}>
                        {test.resumeScore}%
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-8 h-8 text-blue-500 hover:text-blue-700"
                      onClick={() => openCandidateSource(test.jobId)}
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {hasActiveCandidateFilters && !hasAnyFilteredDashboardCandidates && (
        <Card>
          <CardContent className="py-10 text-center text-slate-500">
            No candidates match the current filters.
          </CardContent>
        </Card>
      )}

      {/* ANALYTICS CHARTS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              Applications Trend
            </h2>
          </CardHeader>
          <CardContent className="pt-4">
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={lineChartData}>
                <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
                <Tooltip />
                <Line type="monotone" dataKey="applications" stroke="#2563eb" strokeWidth={3} dot={{ fill: '#2563eb', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              Candidate Distribution
            </h2>
          </CardHeader>
          <CardContent className="pt-4">
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2}>
                    {pieData.map((e, i) => (<Cell key={i} fill={e.color} />))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-slate-400 py-6 text-sm italic">No data available</div>
            )}
          </CardContent>
        </Card>
      </div>

    </div>
  );
}
